import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { TOUCHPOINT_GROUPS } from '../hooks/useAddToLeads';

/**
 * AddToLeadsModal — lets the user add the sender of an email to the World DB
 * "Leads" list, optionally tagging touchpoints that already happened. Mirrors
 * the classic SmartInbox modal but wears the InboxView (`ivx-`) styling.
 *
 * Touchpoint selection is local state; on confirm it hands the selected map
 * back to the parent, which persists it via the useAddToLeads hook.
 */
// Chips describing which of the matched contact's DB fields the lead inherits.
// Only fields that actually carry data are shown, so the preview never lies.
function LinkedFields({ fields }) {
  if (!fields) return null;
  const chips = [];
  if (fields.description) chips.push({ icon: 'fa-align-left', label: 'Description' });
  if (fields.decisionMakers > 0) {
    chips.push({
      icon: 'fa-users',
      label: `${fields.decisionMakers} Decision Maker${fields.decisionMakers > 1 ? 's' : ''}`,
    });
  }
  if (fields.companySize) chips.push({ icon: 'fa-building', label: String(fields.companySize) });
  if (fields.research) chips.push({ icon: 'fa-magnifying-glass', label: 'Research' });
  if (fields.website) chips.push({ icon: 'fa-globe', label: 'Website' });

  if (chips.length === 0) return null;
  return (
    <div className="ivx-leads-linked-fields">
      {chips.map((chip) => (
        <span key={chip.label} className="ivx-leads-linked-chip">
          <i className={`fa ${chip.icon}`} />
          {chip.label}
        </span>
      ))}
    </div>
  );
}

export default function AddToLeadsModal({
  contactName,
  customTouchpoints = [],
  adding,
  linkedContact = null,
  previewLoading = false,
  onConfirm,
  onClose,
}) {
  const [selected, setSelected] = useState({});
  const hasLink = Boolean(linkedContact && linkedContact.matchType);

  const toggle = (key, config) => {
    setSelected((prev) => {
      if (prev[key]) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: config.hasCount ? 1 : true };
    });
  };

  const count = Object.keys(selected).length;

  return ReactDOM.createPortal(
    <div className="ivx-leads-overlay" onClick={onClose}>
      <div className="ivx-leads-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ivx-leads-header">
          <h3>Add to Leads</h3>
          <button type="button" className="ivx-leads-close" onClick={onClose} title="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="ivx-leads-info">
          <div className="ivx-leads-contact">
            <i className="fa fa-user-plus" />
            <span>{contactName || 'Unknown contact'}</span>
          </div>

          {previewLoading && (
            <div className="ivx-leads-link-card ivx-leads-link-loading">
              <i className="fa fa-spinner fa-spin" />
              <span>Checking your database…</span>
            </div>
          )}

          {!previewLoading && hasLink && (
            <div className="ivx-leads-link-card">
              <div className="ivx-leads-link-head">
                <i className="fa fa-link" />
                <span className="ivx-leads-link-name">{linkedContact.businessName}</span>
                {linkedContact.alreadyLead && (
                  <span className="ivx-leads-link-tag">Already a lead</span>
                )}
              </div>
              <p className="ivx-leads-link-sub">
                {linkedContact.matchType === 'email'
                  ? 'Matched an existing contact in your database. Its data stays linked and up to date.'
                  : `Matched a contact from ${linkedContact.domain}. Its database data stays linked and up to date.`}
              </p>
              {linkedContact.consolidatesContact && (
                <p className="ivx-leads-link-note">
                  <i className="fa fa-compress-arrows-alt" /> A thin duplicate contact for this
                  sender will be merged into this lead.
                </p>
              )}
              <LinkedFields fields={linkedContact.fields} />
            </div>
          )}

          {!previewLoading && !hasLink && (
            <div className="ivx-leads-link-card ivx-leads-link-empty">
              <i className="fa fa-circle-plus" />
              <span>No matching database contact — a standalone lead will be created.</span>
            </div>
          )}

          <p className="ivx-leads-hint">Select touchpoints that already happened with this lead (optional)</p>
        </div>

        <div className="ivx-leads-body">
          {Object.entries(TOUCHPOINT_GROUPS).map(([groupKey, group]) => (
            <div key={groupKey} className="ivx-leads-group">
              <div className="ivx-leads-group-header">
                <i className={`${group.isBrand ? 'fab' : 'fa'} ${group.icon}`} />
                <span>{group.label}</span>
              </div>
              <div className="ivx-leads-options">
                {Object.entries(group.items).map(([key, config]) => (
                  <button
                    key={key}
                    type="button"
                    className={`ivx-leads-option ${selected[key] ? 'selected' : ''}`}
                    onClick={() => toggle(key, config)}
                  >
                    <i className={`${config.isBrand ? 'fab' : 'fa'} ${config.icon}`} />
                    <span>{config.label}</span>
                    {selected[key] && <i className="fa fa-check ivx-leads-check" />}
                  </button>
                ))}
              </div>
            </div>
          ))}

          {customTouchpoints.length > 0 && (
            <div className="ivx-leads-group">
              <div className="ivx-leads-group-header">
                <i className="fa fa-star" />
                <span>Custom Touchpoints</span>
              </div>
              <div className="ivx-leads-options">
                {customTouchpoints.map((tp) => {
                  const key = `custom_${tp.id}`;
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`ivx-leads-option ${selected[key] ? 'selected' : ''}`}
                      onClick={() => toggle(key, { hasCount: tp.hasCount, points: tp.points })}
                    >
                      <i className={`fa ${tp.icon || 'fa-tag'}`} />
                      <span>{tp.label}</span>
                      {selected[key] && <i className="fa fa-check ivx-leads-check" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="ivx-leads-footer">
          <button
            type="button"
            className="ivx-leads-confirm"
            disabled={adding}
            onClick={() => onConfirm(selected)}
          >
            {adding
              ? 'Adding…'
              : `${hasLink ? 'Link & Add Lead' : 'Add Lead'}${count > 0 ? ` · ${count} Touchpoint${count > 1 ? 's' : ''}` : ''}`}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
